import React from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { AdminApprovalType } from '@column/api';
import { Button, Toggle } from '@column/column-ui-kit';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { StatusBadge } from '~/components/Table/StatusBadge';
import {
  AdminApprovalReviewDialog,
  ReviewChangesButton,
  useAdminApprovalReview,
  usePendingAdminApprovalsQuery,
} from '~/lib/approvals';
import { useSessionStore } from '~/stores/Session';

const PendingDescription = styled.p`
  margin: 4px 0 0;
  padding-left: 2px;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  font-size: 12px;
  line-height: 18px;
`;

interface Props {
  value: boolean;
  onChange: (value: boolean) => void;
}

export const AdminApprovalConfiguration: React.FC<Props> = ({ value, onChange }) => {
  const { currentPlatform, isAuthorized, loadPlatform } = useSessionStore();
  const {
    formState: { isSubmitting },
  } = useFormContext();
  const canConfigure = isAuthorized({ permission: 'platformInfo', permissionLevel: 'write' });
  const { approvals, isLoading, isError, refetch } = usePendingAdminApprovalsQuery(
    AdminApprovalType.PLATFORM_UPDATE_REQUIRE_USER_CHANGE_APPROVAL,
    canConfigure
  );
  const { openReview, startReview, dialogProps } = useAdminApprovalReview(approvals);
  const pendingApproval = approvals[0];
  const pendingValue =
    pendingApproval?.request.platform_update_require_user_change_approval?.require_user_change_approval;

  return (
    <>
      <AdminApprovalReviewDialog {...dialogProps} hideCounter onReviewed={() => loadPlatform({})} />
      <PageActions>
        <ReviewChangesButton onClick={startReview} />
        <Button type="submit" form="platform-settings-form" size="small" isDisabled={isSubmitting}>
          Save changes
        </Button>
      </PageActions>
      <div id="user-change-approval-control" className="flex min-h-6 flex-wrap items-center gap-2">
        {!pendingApproval && (
          <button
            type="button"
            id="user-change-approval"
            className="inline-flex items-center"
            role="switch"
            aria-label="User Access Changes"
            aria-checked={value}
            disabled={!currentPlatform || !canConfigure || isLoading || isError || isSubmitting || !!pendingApproval}
            onClick={() => onChange(!value)}
          >
            <Toggle
              isChecked={value}
              isDisabled={!canConfigure || isLoading || isError || isSubmitting || !!pendingApproval}
              style={{ pointerEvents: 'none' }}
            />
          </button>
        )}
        {pendingApproval && (
          <>
            <StatusBadge tone="warning">Pending approval</StatusBadge>
            <Button type="button" size="tiny" variant="secondary" onClick={() => openReview(pendingApproval)}>
              {pendingApproval.can_review ? 'Review' : 'View'}
            </Button>
          </>
        )}
      </div>
      {pendingApproval && (
        <PendingDescription>
          This setting will remain {pendingValue ? 'disabled' : 'enabled'} until an authorized user approves this
          change.
        </PendingDescription>
      )}
      {isError && (
        <div role="alert" className="mt-3 flex items-center gap-3 text-sm">
          Unable to load pending setting changes.
          <Button type="button" size="tiny" variant="secondary" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      )}
    </>
  );
};
